import { Album, MediaApiFetchParamCreator } from '@bpm-web-app/download-api-sdk';
import { downloadConfig } from '../config';
import { SearchPaginatedQuery } from '../typings/search-query';

export interface FeedPagination {
    total: number;
    page?: number;
    limited?: number;
    skipped?: number;
}

export interface FeedResponse {
    data: Album[];
    pagination?: FeedPagination;
}

export class Feed {
    /**
     * Your Feed — newest-added albums from the artists the current user follows.
     *
     * The backend (`GET /v1/feed`) derives the followed-artist set from the authenticated session,
     * so `artist` is intentionally NOT sent — that also keeps the URL short no matter how many
     * artists a user follows (the reason the old client-side chunking existed).
     *
     * INTERIM: reuses the generated `/albums` request builder for identical auth, headers and query
     * serialization, then repoints the path to `/feed`. Replace with a generated `getFeed` SDK
     * method once the download swagger includes the route (see docs/feed swagger snippet).
     */
    static async getFeed(args: SearchPaginatedQuery): Promise<FeedResponse> {
        const fetchArgs = MediaApiFetchParamCreator(downloadConfig).searchAlbum(
            args.limit,
            args.skip,
            args.category,
            args.library,
            args.genre as string,
            undefined, // artist — derived server-side from the session, never sent by the client
            args.hide_remix ? '1' : '0',
            args.hide_originals ? '1' : '0',
            args.hide_exclusives ? '1' : '0',
            args.hide_explicit ? '1' : '0',
            args.hide_prev_downloaded ? '1' : '0',
            args.version as string,
            args.key as string,
            args.keyType,
            args.bpm_min,
            args.bpm_max,
            args.sort_by,
            args.term,
            args.date,
            args.remixer
        );

        const url = `${downloadConfig.basePath || ''}${fetchArgs.url.replace(/^\/albums/, '/feed')}`;
        const response = await fetch(url, fetchArgs.options);
        if (response.status >= 200 && response.status < 300) {
            return response.json();
        }
        throw new Error(`Feed request failed with status ${response.status}`);
    }
}
